import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { HeaderNav } from "@/components/HeaderNav";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Building2, Ghost } from "lucide-react";

export default function Join() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { user, ready, register, refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    api.get(`/invitations/${token}`).then((r) => setInfo(r.data)).catch(() => setInfo(null)).finally(() => setLoading(false));
  }, [token]);

  const acceptAsExisting = async () => {
    setBusy(true);
    try {
      await api.post("/invitations/accept", { token });
      await refresh();
      toast.success("Invitation accepted");
      navigate("/consent");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const registerAndAccept = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await register({ name: form.name, email: info.email, password: form.password, invitation_token: token });
      toast.success("Account created");
      navigate("/consent");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  if (loading || !ready) return (<div><HeaderNav /><div className="max-w-lg mx-auto px-5 pt-24 cg-eyebrow animate-pulse">Loading invitation…</div></div>);

  if (!token || !info || !info.valid) {
    return (
      <div><HeaderNav />
        <div className="max-w-lg mx-auto px-5 pt-24 text-center cg-fade-up" data-testid="join-invalid">
          <Ghost className="w-8 h-8 text-ghostslate mx-auto mb-4" />
          <h1 className="font-serif text-3xl text-[#F3F4F6] mb-2">This invitation isn't available</h1>
          <p className="text-sm text-[#9CA3AF] mb-6">{info?.used ? "It has already been used." : "The link may have expired or is invalid."}</p>
          <Link to="/login" className="cg-link">Sign in instead</Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <HeaderNav />
      <div className="max-w-lg mx-auto px-5 pt-16 pb-16 cg-fade-up" data-testid="join-page">
        <p className="cg-eyebrow mb-3">You've been invited</p>
        <h1 className="font-serif text-4xl text-[#F3F4F6] mb-4">Join {info.campaign_name}</h1>
        <div className="cg-card p-5 mb-6 flex items-center gap-3">
          <Building2 className="w-5 h-5 text-brass" />
          <div>
            <p className="text-[#F3F4F6]">{info.organization_name}</p>
            <p className="text-sm text-[#9CA3AF] font-mono">{info.email}</p>
          </div>
        </div>
        <p className="text-sm text-[#9CA3AF] mb-6 leading-relaxed">You'll receive your own personal Authority Portrait. Your individual answers and personal report won't be shared with the organization or group that invited you. They'll only see combined results once enough people have taken part to protect individual privacy.</p>

        {user && user.role === "participant" ? (
          <button data-testid="join-accept-button" onClick={acceptAsExisting} disabled={busy}
            className="w-full bg-brass text-obsidian font-medium py-3 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-60">
            {busy ? "Joining…" : "Join & continue"}
          </button>
        ) : user ? (
          <p className="text-sm text-terracotta" data-testid="join-wrong-role">You're signed in as {user.role.replace("_", " ")}. Please sign out and use this link as a participant.</p>
        ) : (
          <form onSubmit={registerAndAccept} className="cg-card p-6 space-y-4" data-testid="join-register-form">
            <h2 className="font-serif text-xl text-[#F3F4F6]">Create your account</h2>
            <div>
              <label className="text-sm text-[#9CA3AF]">Full name</label>
              <input data-testid="join-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-4 py-3 text-[#F3F4F6] focus:border-brass outline-none" />
            </div>
            <div>
              <label className="text-sm text-[#9CA3AF]">Email</label>
              <input value={info.email} disabled className="mt-1.5 w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-4 py-3 text-[#6B7280] font-mono" />
            </div>
            <div>
              <label className="text-sm text-[#9CA3AF]">Password <span className="text-[#6B7280]">(min 8)</span></label>
              <input data-testid="join-password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-4 py-3 text-[#F3F4F6] focus:border-brass outline-none" />
            </div>
            {error && <p className="text-sm text-terracotta" data-testid="join-error">{error}</p>}
            <button data-testid="join-register-button" disabled={busy}
              className="w-full bg-brass text-obsidian font-medium py-3 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-60">
              {busy ? "Creating…" : "Create account & begin"}
            </button>
            <p className="text-xs text-[#6B7280] text-center">Already have an account? <Link to="/login" className="cg-link">Sign in</Link>, then reopen this link.</p>
          </form>
        )}
      </div>
    </div>
  );
}
